<template>
<div>
    <!-- Mesmo filtro do /plans (Search.vue) para manter paridade -->
    <Search
        v-if="showSearchBar"
        v-model="planType"
        :show-search="showSearchInput"
        :categories="showCategoryFilter ? categories : null"
        :plan-categories="selectedCategories"
        :plan-facets="visibleFacets"
        :selected-facets="selectedFacets"
        :filters="showTypes"
        :currency="false"
        :available-years="availableYears"
        :selected-year="selectedYear"
        no-fixed
        @searchInput="onSearchValue"
        @categoryFilter="onCategoryFilter"
        @facetFilter="onFacetFilter"
        @yearFilter="onYearFilter"
        @orderFilter="onOrder"
        @cleanFilter="cleanFilter"
    />
    <div
        v-if="loaded"
        class="nch-mt-5"
        :class="[containerClass, 'nch--plans-cards', `nch--plans-cards--${normalizedConfig.card_style}`]"
    >
        <div class="d-flex flex-wrap align-items-stretch">
            <PlansInfo
                v-for="plan in plans"
                :key="plan.id"
                :plan="plan"
                :plans="plans.length"
                :color="pageColor"
                :layout="customLayout"
                :proceed="proceed"
                :category-color="plan.category_color || getCategoryColor(plan)"
                :show-price="!!normalizedConfig.show_price"
                :show-description="!!normalizedConfig.show_description"
            />
        </div>
    </div>
    <Empty
        v-if="empty"
        :color="pageColor"
        class="mt-9"
        icon="mdi mdi-package-variant"
    >
        <template #title><h1 class="font-weight-medium mb-0">{{ $t('empty.filter_title') }}</h1></template>
        <template #subtitle><p>{{ $t('empty.filter_subtitle') }}.</p></template>
    </Empty>
    <div v-if="!empty" class="container d-flex justify-content-center align-items-center my-5">
        <base-pagination
            class="mb-0"
            v-if="meta"
            :page-count="meta.last_page"
            v-model="meta.current_page"
            @input="onChangePage"
            type="#525F7F"
        />
    </div>
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import PlansInfo from '../PlansComponents/PlansInfo';
import BasePagination from '../../argon/components/BasePagination';
import Empty from '../Common/Empty';
import Search from '../Search';
import debounce from '../../helpers/helpers';

export default {
    name: 'PlansFilterPage',
    components: { PlansInfo, BasePagination, Empty, Search },

    props: {
        config: { type: Object, default: () => ({}) },
        pageColor: { type: Object, required: true },
        layout: { type: Object, required: true },
        categories: { type: Array, default: () => [] },
        planFacets: { type: Array, default: () => [] },
        availableYears: { type: Array, default: () => [] },
    },

    data() {
        return {
            data: { data: [] },
            perPage: 6,
            search: '',
            planType: [],
            selectedCategories: [],
            selectedFacets: {},
            selectedYear: '',
            order: '',
            empty: true,
            loaded: false,
            proceed: false,
        };
    },

    computed: {
        ...mapGetters({ language: 'LANGUAGE', currency: 'CURRENCY' }),

        normalizedConfig() {
            return {
                show_search: true,
                show_category_filter: true,
                show_types: true,
                show_price: true,
                show_description: true,
                columns: 3,
                card_style: 'elevated',
                ...this.config,
            };
        },

        showSearchInput() { return this.normalizedConfig.show_search !== false && !!this.normalizedConfig.show_search; },
        showCategoryFilter() { return this.normalizedConfig.show_category_filter !== false && !!this.normalizedConfig.show_category_filter; },
        showTypes() { return this.normalizedConfig.show_types !== false && !!this.normalizedConfig.show_types; },
        showSearchBar() { return this.showSearchInput || this.showCategoryFilter || this.showTypes || this.visibleFacets.length > 0; },

        // Facetas a exibir: se a config define um subconjunto (facets), filtra por ele;
        // senão exibe todas as facetas definidas no tenant. Sempre só com valores.
        visibleFacets() {
            const allowed = this.normalizedConfig.facets;
            return (this.planFacets || [])
                .filter(f => f && f.key && Array.isArray(f.values) && f.values.length)
                .filter(f => !Array.isArray(allowed) || !allowed.length || allowed.includes(f.key));
        },
        plans() { return this.data.data || []; },
        meta() { return this.data.meta; },
        containerClass() {
            return this.$mq === 'sm' || this.$mq === 'md' ? 'container-fluid' : 'container';
        },
        customLayout() {
            return {
                ...this.layout,
                column_count: this.normalizedConfig.columns ? parseInt(this.normalizedConfig.columns, 10) : (this.layout.column_count || 3),
            };
        },
    },

    watch: {
        search() { debounce(() => this.fetchPlans(), 500).call(); },
        planType() { debounce(() => { if (this.meta) this.meta.current_page = 1; this.fetchPlans(); }, 300).call(); },
        selectedCategories() { debounce(() => { if (this.meta) this.meta.current_page = 1; this.fetchPlans(); }, 300).call(); },
        selectedFacets: { deep: true, handler() { debounce(() => { if (this.meta) this.meta.current_page = 1; this.fetchPlans(); }, 300).call(); } },
        selectedYear() { debounce(() => { if (this.meta) this.meta.current_page = 1; this.fetchPlans(); }, 300).call(); },
        currency() { debounce(() => this.fetchPlans(), 100).call(); },
        plans() { this.empty = !this.plans.length; },
    },

    mounted() {
        this.fetchPlans();
    },

    methods: {
        getCategoryColor(plan) {
            if (!plan.category_id || !this.categories) return null;
            const category = this.categories.find(c => c.id == plan.category_id);
            return category ? category.color : null;
        },
        onSearchValue(value) { this.search = value; },
        onCategoryFilter(categories) { this.selectedCategories = (categories || []).filter(c => c); },
        onFacetFilter(facets) { this.selectedFacets = facets; },
        onYearFilter(year) { this.selectedYear = year; },
        onOrder(value) { this.order = value; this.fetchPlans(); },
        cleanFilter() {
            this.search = '';
            this.selectedCategories = [];
            this.selectedFacets = {};
            this.selectedYear = '';
            this.order = '';
            this.fetchPlans();
        },
        onChangePage() { this.fetchPlans(); },

        fetchPlans() {
            this.$loading.show();
            const filterCategories = this.normalizedConfig.categories && this.normalizedConfig.categories.length
                ? this.normalizedConfig.categories
                : null;

            const activeCategories = this.selectedCategories.length
                ? this.selectedCategories
                : filterCategories;

            window.axios.get(`/api/public/plans/${this.language.iso}/${this.perPage}`, {
                params: {
                    page: this.meta ? this.meta.current_page : 1,
                    column: this.order ? 'plans.value' : 'plans.id',
                    order: this.order || 'ASC',
                    currency: this.currency.code || null,
                    type: this.planType.length > 0 ? this.planType : null,
                    categories: activeCategories,
                    facets: Object.keys(this.selectedFacets).length ? this.selectedFacets : null,
                    year: this.selectedYear || null,
                    search: this.search,
                },
            }).then(res => {
                this.data = res.data;
            }).catch(err => console.error(err))
              .finally(() => { this.$loading.close(); this.loaded = true; });
        },
    },
};
</script>

<style scoped>
.nch-mt-5 { margin-top: 15px; }

/* ── Search bar ── */
.nch--plans-searchbar {
    padding: 16px 0 12px;
    border-bottom: 1px solid var(--page-border, rgba(0,0,0,0.08));
    margin-bottom: 20px;
}

.nch--plans-searchbar__inner {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

.nch--plans-search-field {
    position: relative;
    display: flex;
    align-items: center;
    background: var(--page-surface, #f5f5f5);
    border: 1.5px solid var(--page-border, #e0e0e0);
    border-radius: 10px;
    padding: 0 12px;
    height: 44px;
    min-width: 220px;
    transition: border-color 0.18s, box-shadow 0.18s;
}

.nch--plans-search-field:focus-within {
    border-color: var(--page-accent, #1976d2);
    box-shadow: 0 0 0 3px rgba(25,118,210,0.1);
}

.nch--plans-search-icon {
    color: var(--page-text-secondary, #9e9e9e);
    font-size: 0.875rem;
    margin-right: 8px;
    flex-shrink: 0;
}

.nch--plans-search-input {
    background: transparent;
    border: none;
    outline: none;
    color: var(--page-text, #212121);
    font-size: 0.9rem;
    flex: 1;
    min-width: 0;
}

.nch--plans-search-input::placeholder {
    color: var(--page-text-secondary, #9e9e9e);
}

.nch--plans-search-clear {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--page-text-secondary, #9e9e9e);
    padding: 0;
    margin-left: 4px;
    display: flex;
    align-items: center;
    font-size: 1rem;
    transition: color 0.15s;
}

.nch--plans-search-clear:hover { color: var(--page-text, #212121); }

.nch--plans-searchbar__spacer { flex: 1; }

/* Category chips */
.nch--plans-categories {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

/* Facetas */
.nch--plans-facets {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--page-border, rgba(0,0,0,0.08));
}
.nch--plans-facet {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}
.nch--plans-facet__label {
    font-size: .75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: var(--page-text-secondary, #9e9e9e);
    min-width: 92px;
}
.nch--plans-facet__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.nch--plans-cat-chip {
    padding: 6px 14px;
    border-radius: 99px;
    border: 1.5px solid var(--page-border, #e0e0e0);
    background: transparent;
    color: var(--page-text, #424242);
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    white-space: nowrap;
}

.nch--plans-cat-chip:hover {
    border-color: var(--page-accent, #1976d2);
    color: var(--page-accent, #1976d2);
}

.nch--plans-cat-chip--active {
    background: var(--page-accent, #1976d2) !important;
    border-color: var(--page-accent, #1976d2) !important;
    color: var(--page-accent-text, #fff) !important;
}

/* Order buttons */
.nch--plans-order {
    display: flex;
    gap: 4px;
}

.nch--plans-order-btn {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    border: 1.5px solid var(--page-border, #e0e0e0);
    background: transparent;
    color: var(--page-text-secondary, #9e9e9e);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.nch--plans-order-btn:hover {
    border-color: var(--page-accent, #1976d2);
    color: var(--page-accent, #1976d2);
}

.nch--plans-order-btn--active {
    background: var(--page-accent, #1976d2) !important;
    border-color: var(--page-accent, #1976d2) !important;
    color: var(--page-accent-text, #fff) !important;
}

@media (max-width: 600px) {
    .nch--plans-searchbar__spacer { display: none; }
    .nch--plans-search-field { width: 100%; }
    .nch--plans-categories { width: 100%; }
}

/*
 * OUTLINE: card with white/surface bg and border
 * $bgColor sets backgroundColor+borderColor via inline style — !important overrides both
 */
.nch--plans-cards--outline :deep(.card) {
    background: var(--page-surface, #ffffff) !important;
    background-color: var(--page-surface, #ffffff) !important;
    border: 1.5px solid var(--page-border, rgba(0,0,0,0.14)) !important;
    border-color: var(--page-border, rgba(0,0,0,0.14)) !important;
    box-shadow: none !important;
}

.nch--plans-cards--outline :deep(.card h4),
.nch--plans-cards--outline :deep(.card h3),
.nch--plans-cards--outline :deep(.card h2),
.nch--plans-cards--outline :deep(.card h1),
.nch--plans-cards--outline :deep(.card p),
.nch--plans-cards--outline :deep(.card .display-2) {
    color: var(--page-text, #212121) !important;
}

.nch--plans-cards--outline :deep(.card .extra-info) {
    color: var(--page-text-secondary, #616161) !important;
}

/*
 * FLAT: transparent card, no border, no shadow
 * Same override for inline styles via !important
 */
.nch--plans-cards--flat :deep(.card) {
    background: transparent !important;
    background-color: transparent !important;
    border: none !important;
    border-color: transparent !important;
    box-shadow: none !important;
}

.nch--plans-cards--flat :deep(.card-header),
.nch--plans-cards--flat :deep(.card-footer),
.nch--plans-cards--flat :deep(.card-body) {
    background: transparent !important;
    background-color: transparent !important;
}

.nch--plans-cards--flat :deep(.card h4),
.nch--plans-cards--flat :deep(.card h3),
.nch--plans-cards--flat :deep(.card h2),
.nch--plans-cards--flat :deep(.card h1),
.nch--plans-cards--flat :deep(.card p),
.nch--plans-cards--flat :deep(.card .display-2) {
    color: var(--page-text, #212121) !important;
}

.nch--plans-cards--flat :deep(.card .extra-info) {
    color: var(--page-text-secondary, #616161) !important;
}

/* Separator line adapts to page theme */
.nch--plans-cards--flat :deep(.card .card-header),
.nch--plans-cards--outline :deep(.card .card-header) {
    border-bottom: 1px solid var(--page-border, rgba(0,0,0,0.1)) !important;
}
</style>
